"use client";

import { useEffect, useState } from "react";
import {
  CYPH_BUBBLE_CHART,
  CYPH_CHART_LEAD_SUB,
  type CyphTrackerSections,
} from "@/constants/cypherpunkTracker";
import type { CypherpunkTrackerPayload } from "@/types/cypherpunkTrackerData";
import {
  formatDateLabel,
  formatNumberFixed,
  formatUsdCompact,
  formatZec,
} from "@/lib/cypherpunk/format";
import {
  CypherpunkBubbleChartDeferred,
  CypherpunkCumulativeChartDeferred,
  CypherpunkNetworkChartDeferred,
} from "./CyphChartsDeferred";
import CypherpunkTrackerBody from "./CypherpunkTrackerBody";
import FivePercentProgress from "./FivePercentProgress";
import NetworkSharePosition from "./NetworkSharePosition";
import TopMetricLozenges from "./TopMetricLozenges";
import { CypherpunkTrackerProvider } from "./CypherpunkTrackerContext";
import "./cypherpunk-tracker.css";

const TRACKER_DATA_UNAVAILABLE =
  "Live tracker data is temporarily unavailable. Metrics and charts will load when the data service is reachable.";

const TRACKER_RETRY_MS = 30_000;

function CyphNum({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="cyph-num">{children}</span>;
}

function reserveValueParts(usd: number): { main: string; unit: string } {
  if (usd >= 1_000_000) {
    return { main: formatNumberFixed(usd / 1_000_000), unit: "million" };
  }
  if (usd >= 1_000) {
    return { main: formatNumberFixed(usd / 1_000), unit: "thousand" };
  }
  return { main: formatNumberFixed(usd), unit: "" };
}

function TrackerContent({
  data,
  sections,
  body,
}: {
  data: CypherpunkTrackerPayload;
  sections: CyphTrackerSections;
  body?: string | null;
}): React.ReactElement {
  const [, chart2Label, chart3Label] = sections.chartLabels;
  const reserve = reserveValueParts(data.display.reserveValueUsd);
  const gainSign = (data.display.unrealizedGainPct ?? 0) >= 0 ? "▲" : "▼";
  const gainPct = formatNumberFixed(Math.abs(data.display.unrealizedGainPct ?? 0));

  return (
    <div className="cyph-tracker">
      <TopMetricLozenges data={data} />

      <FivePercentProgress data={data} />

      <div className="card cyph-defer-paint cyph-defer-paint--tall">
        <div className="card-head">
          <div>
            <div className="card-title">{CYPH_BUBBLE_CHART.title}</div>
            <div className="card-sub">{CYPH_BUBBLE_CHART.subtitle}</div>
          </div>
        </div>
        <div className="data-head">
          <div>
            <div className="lead-label mb-1.5">Reserve value</div>
            <div className="lead-value text-[28px] sm:text-[32px] font-medium text-accent-gold tracking-[-1px]">
              $<span className="cyph-num">{reserve.main}</span>
              {reserve.unit ? (
                <span className="text-[18px] sm:text-[20px] font-medium text-gold ml-1.5">{reserve.unit}</span>
              ) : null}
            </div>
            <div className="lead-sub mt-2 text-xs">
              <span className="text-accent-gold font-medium">
                <span className="cyph-num">{formatZec(data.summary.totalZecHeld)}</span> ZEC
              </span>
              <span className="text-foreground/50 mx-1.5">·</span>
              <span className="text-foreground/72">
                Avg cost: <span className="text-foreground">
                  {data.summary.avgCostBasisUsd != null ? (
                    <span className="cyph-num">{formatUsdCompact(data.summary.avgCostBasisUsd)}</span>
                  ) : (
                    "—"
                  )}
                </span>
              </span>
              {data.display.unrealizedGainPct != null ? (
                <>
                  <span className="text-foreground/50 mx-1.5">·</span>
                  <span className="text-accent-green font-medium">
                    {gainSign} <span className="cyph-num">{gainPct}</span>%{" "}
                  </span>
                  <span className="text-foreground/50">
                    (<span className="cyph-num">{formatUsdCompact(data.display.unrealizedGainUsd)}</span> unrealized)
                  </span>
                </>
              ) : null}
            </div>
            <div className="text-[12px] text-foreground/50 mt-2">
              As of {formatDateLabel(data.market.asOfDate)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="summary-inset">
              <div className="summary-cell">
                <div className="summary-label">Purchase events</div>
                <div className="summary-value">
                  <span className="cyph-num">{data.summary.purchaseCount}</span>
                </div>
              </div>
              <div className="summary-cell">
                <div className="summary-label">Total $ deployed</div>
                <div className="summary-value">
                  <span className="cyph-num">{formatUsdCompact(data.summary.totalCostUsd)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CypherpunkBubbleChartDeferred />
      </div>

      <NetworkSharePosition data={data} />

      <div className="card cyph-defer-paint">
        <div className="data-head">
          <div>
            <div className="lead-label mb-1.5">{chart2Label}</div>
            <div className="flex items-end gap-2 lead-value text-[28px] sm:text-[32px] font-medium text-accent-gold tracking-[-1px]">
              <CyphNum>{formatZec(data.summary.totalZecHeld)}</CyphNum>{" "}
              <span className="top-[-2px] relative text-[18px] sm:text-[20px] text-gold font-medium">ZEC</span>
            </div>
            <div className="lead-sub">{CYPH_CHART_LEAD_SUB.cumulative}</div>
          </div>
          <div className="delta-corner">
            <div className="delta-value">
              {data.display.lastPurchaseZec >= 0 ? "+" : "-"}
              <CyphNum>{formatZec(Math.abs(data.display.lastPurchaseZec))}</CyphNum> ZEC
            </div>
            <div className="delta-label">last purchase</div>
          </div>
        </div>
        <CypherpunkCumulativeChartDeferred />
        {/* <div className="watermark">www.zcashtracker.com</div> */}
      </div>

      <div className="card cyph-defer-paint">
        <div className="data-head">
          <div>
            <div className="lead-label mb-1.5">{chart3Label}</div>
            <div className="lead-value text-[28px] sm:text-[32px] font-medium text-accent-gold tracking-[-1px]">
              <CyphNum>{formatNumberFixed(data.goal.currentNetworkPct)}</CyphNum>
              <span className="text-[18px] pl-1 sm:text-[20px] text-gold font-medium">%</span>
            </div>
            <div className="lead-sub">{CYPH_CHART_LEAD_SUB.network}</div>
          </div>
          <div className="delta-corner">
            <div className="delta-value">
              {data.goal.currentNetworkPct >= 0 ? "+" : ""}
              <CyphNum>{formatNumberFixed(Math.abs(data.goal.currentNetworkPct))}</CyphNum>pp
            </div>
            <div className="delta-label">since first buy</div>
          </div>
        </div>
        <CypherpunkNetworkChartDeferred />
        {/* <div className="watermark">www.zcashtracker.com</div> */}
      </div>

      <div className="card cyph-defer-paint">
        <div className="card-head">
          <div>
            <div className="card-title">{sections.tableTitle}</div>
            <div className="card-sub">{sections.tableSubtext}</div>
          </div>
          <div className="card-tag">
            <CyphNum>{data.summary.purchaseCount}</CyphNum> EVENTS
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="purchases">
            <thead>
              <tr>
                <th>Date</th>
                <th className="num">ZEC Acquired</th>
                <th className="num">Avg Price</th>
                <th className="num">USD Spent</th>
                <th className="num">Cumulative ZEC</th>
                <th className="num">% of Supply</th>
              </tr>
            </thead>
            <tbody>
              {data.tableRows.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td className="num orange">{row.acquired}</td>
                  <td className="num">{row.avgPrice}</td>
                  <td className="num">{row.usd}</td>
                  <td className="num">{row.cumulative}</td>
                  <td className="num">{row.supply}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {body ? <CypherpunkTrackerBody body={body} /> : null}

      <footer className="pagefoot">
        <span>Data: cypherpunk.com · CoinGecko · Refreshed every 15 min</span>
      </footer>
    </div>
  );
}

function TrackerUnavailable({
  sections,
  body,
  loading = false,
}: {
  sections: CyphTrackerSections;
  body?: string | null;
  loading?: boolean;
}): React.ReactElement {
  const [, chart2Label, chart3Label] = sections.chartLabels;

  return (
    <div className="cyph-tracker">
      <div className="card cyph-defer-paint">
        <p className="text-[13px] text-white/86 font-kode-mono leading-relaxed">
          {loading ? "Loading tracker data…" : TRACKER_DATA_UNAVAILABLE}
        </p>
      </div>

      <div className="card cyph-defer-paint cyph-defer-paint--tall">
        <div className="card-head">
          <div>
            <div className="card-title">{CYPH_BUBBLE_CHART.title}</div>
            <div className="card-sub">{CYPH_BUBBLE_CHART.subtitle}</div>
          </div>
        </div>
        <CypherpunkBubbleChartDeferred />
        <div className="watermark">www.zcashtracker.com</div>
      </div>

      <div className="card cyph-defer-paint">
        <div className="lead-label mb-3">{chart2Label}</div>
        <CypherpunkCumulativeChartDeferred />
        <div className="watermark">www.zcashtracker.com</div>
      </div>

      <div className="card cyph-defer-paint">
        <div className="lead-label mb-3">{chart3Label}</div>
        <CypherpunkNetworkChartDeferred />
        <div className="watermark">www.zcashtracker.com</div>
      </div>

      <div className="card cyph-defer-paint">
        <div className="card-head">
          <div>
            <div className="card-title">{sections.tableTitle}</div>
            <div className="card-sub">{sections.tableSubtext}</div>
          </div>
        </div>
        <p className="text-[12px] text-white/65 font-kode-mono py-6 text-center">
          Purchase history unavailable.
        </p>
      </div>

      {body ? <CypherpunkTrackerBody body={body} /> : null}

      <footer className="pagefoot">
        <span>Data: cypherpunk.com · CoinGecko · Refreshed every 15 min</span>
      </footer>
    </div>
  );
}

export default function CypherpunkTrackerClient({
  data: initialData,
  body,
  sections,
}: {
  data: CypherpunkTrackerPayload | null;
  body?: string | null;
  sections: CyphTrackerSections;
}): React.ReactElement {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    initialData ? null : TRACKER_DATA_UNAVAILABLE,
  );

  useEffect(() => {
    if (initialData !== null) return;

    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let isFirstFetch = true;

    const fetchTracker = async () => {
      if (isFirstFetch) setLoading(true);
      try {
        const res = await fetch("/api/cypherpunk-tracker");
        if (cancelled) return;
        if (!res.ok) {
          setError(TRACKER_DATA_UNAVAILABLE);
          return;
        }
        const payload = (await res.json()) as CypherpunkTrackerPayload;
        if (cancelled) return;
        setData(payload);
        setError(null);
        if (poll) {
          clearInterval(poll);
          poll = undefined;
        }
      } catch {
        if (!cancelled) setError(TRACKER_DATA_UNAVAILABLE);
      } finally {
        if (!cancelled) {
          setLoading(false);
          isFirstFetch = false;
        }
      }
    };

    void fetchTracker();
    poll = setInterval(() => {
      void fetchTracker();
    }, TRACKER_RETRY_MS);

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
    };
  }, [initialData]);

  return (
    <CypherpunkTrackerProvider
      value={{
        data,
        loading,
        error,
      }}
    >
      {data ? (
        <TrackerContent data={data} sections={sections} body={body} />
      ) : (
        <TrackerUnavailable sections={sections} body={body} loading={loading} />
      )}
    </CypherpunkTrackerProvider>
  );
}
